import { type Handle, css, ref } from 'remix/ui'
import { normalizeRedirectTo } from '#universal/safe-redirect.ts'
import { navigate, readCurrentRouterHref } from '#client/client-router.tsx'
import { discardRenderPrefetches } from '#client/intent-prefetch.ts'
import { tryConsumeRouteLoaderData } from '#client/loader-data-context.tsx'
import {
	createRouteData,
	renderRoutePendingStatus,
	routeDataRedirect,
} from '#client/route-data.tsx'
import { readRouterSearch } from '#client/router-location.tsx'
import { type AccountStatus } from '#client/routes/account-approval-shared.ts'
import {
	routeLoaderRedirect,
	type RouteLoaderResult,
} from '#client/route-loader.ts'
import {
	closeOnboardingMcpOAuthPopupIfOpened,
	listenForOnboardingMcpOAuthDone,
} from '#client/mcp-oauth-popup.ts'
import { routes } from '#universal/routes.ts'
import {
	isOnboardingPagePath,
	onboardingIndexRedirectHref,
	onboardingSecondAgentHref,
	onboardingStepPaths,
	onboardingWizardStepByNumber,
	onboardingWizardStepHref,
	parseOnboardingPathname,
	resolveOnboardingFirstAgentKind,
	type OnboardingWizardStepNumber,
} from '#universal/onboarding-process.ts'
import { resolveOnboardingStep3SelectedAgent } from '#universal/onboarding-agent-ecosystems.ts'
import {
	fetchOnboardingPayload,
	type OnboardingPayload,
} from '#client/routes/onboarding-payload.ts'
import {
	type OnboardingAgentChooserPick,
	onboardingAgentLabel,
	onboardingDataHref,
} from '#client/routes/onboarding-mcp-clients.ts'
import {
	rememberOnboardingAgentChooser,
	resolveOnboardingAgentChooser,
} from '#client/routes/onboarding-agent-chooser-session.ts'
import {
	readRememberedOnboardingSelectedAgent,
	rememberOnboardingSelectedAgent,
} from '#client/routes/onboarding-selected-agent-session.ts'
import {
	renderAccessPanel,
	renderConnectAgentPanel,
	renderSecondAgentPanel,
} from '#client/routes/onboarding-wizard-panels.tsx'
import { renderWizardStepsNav } from '#client/routes/onboarding-wizard-chrome.tsx'
import { ProviderIcon } from '#client/provider-icons.tsx'
import { resolveOnboardingPendingVerificationPath } from '#client/routes/onboarding-redirect.ts'
import { colors, transitions, typography } from '#universal/styles/tokens.ts'
import { getGhostButtonCss } from '#universal/styles/style-primitives.ts'

/**
 * Onboarding wizard: shirt-pattern head, three-step stepper (Connect your
 * agent · Make something useful · Connect a second agent), one surface panel
 * at a time with hand-tilted mascot art. Step 1 picks one agent. Step 2 is
 * one prompt that tells the agent to retrieve the onboarding guide. Step 3
 * looks like Step 1 and greys the same-ecosystem family plus every already
 * connected named host, then folds in a portability proof that reuses what
 * Step 2 made.
 */

type OnboardingStep = OnboardingWizardStepNumber

type OnboardingPagePayloads = {
	onboarding: OnboardingPayload
	onboardingAgentChooser?: OnboardingAgentChooserPick
	/** Router/SSR snapshots merge into progress already seen; live fetches replace it. */
	source: 'live' | 'snapshot'
}

function isOnboardingPath(href: string) {
	return isOnboardingPagePath(new URL(href, 'http://localhost').pathname)
}

function readOnboardingLocation(href: string) {
	const url = new URL(href, 'http://localhost')
	return parseOnboardingPathname(url.pathname)
}

function readOnboardingRedirectTo(handle: Handle) {
	return normalizeRedirectTo(
		new URLSearchParams(readRouterSearch(handle)).get('redirectTo'),
	)
}

export async function onboardingRouteLoader(
	url: URL,
	signal: AbortSignal,
): Promise<RouteLoaderResult> {
	const redirectTo = normalizeRedirectTo(url.searchParams.get('redirectTo'))
	const payload = await fetchOnboardingPayload(signal)
	if (!payload) {
		throw new Error('Unable to load onboarding.')
	}
	if (payload.loggedIn && !payload.emailVerified) {
		return routeLoaderRedirect(
			resolveOnboardingPendingVerificationPath(redirectTo),
		)
	}
	if (url.pathname === onboardingStepPaths.index) {
		return routeLoaderRedirect(
			onboardingIndexRedirectHref(url.search, {
				hasMcpClient: payload.hasMcpClient,
				hasAccessWin: payload.hasAccessWin,
				hasSecondMcpClient: payload.hasSecondMcpClient,
			}),
		)
	}
	const location = parseOnboardingPathname(url.pathname)
	if (location && !location.valid) {
		return routeLoaderRedirect(
			onboardingWizardStepHref(location.step, url.search),
		)
	}
	return {
		onboarding: payload,
		onboardingAgentChooser: resolveOnboardingAgentChooser(),
	}
}

function mergeConnectedAgents(
	current: OnboardingPayload['connectedAgents'],
	incoming: OnboardingPayload['connectedAgents'] | undefined,
) {
	const next = incoming ?? []
	if (current.length === 0) return next
	if (next.length === 0) return current
	const byId = new Map(current.map((agent) => [agent.clientId, agent]))
	for (const agent of next) {
		byId.set(agent.clientId, agent)
	}
	return [...byId.values()]
}

function sameConnectedAgents(
	left: OnboardingPayload['connectedAgents'] | undefined,
	right: OnboardingPayload['connectedAgents'],
) {
	const incoming = left ?? []
	if (incoming.length !== right.length) return false
	return incoming.every((agent, index) => {
		const current = right[index]
		return (
			current !== undefined &&
			agent.clientId === current.clientId &&
			agent.label === current.label &&
			agent.kind === current.kind
		)
	})
}

export function OnboardingRoute(handle: Handle) {
	let status: AccountStatus = 'loading'
	let message: string | null = null
	let loggedIn = false
	let mcpServerUrl = ''
	let mcpHighlights: OnboardingPayload['mcpHighlights'] = {}
	let discoveryPrompt = ''
	let hasMcpClient = false
	let hasAccessWin = false
	let hasSecondMcpClient = false
	let secondAgentGiftActive = false
	let connectedAgents: OnboardingPayload['connectedAgents'] = []
	let accessWinMemorySubject: string | null = null
	let persistedPackageName: string | null = null
	let initializedStep = false
	let pendingAdvanceToAccess = false
	// Panel entrances only play for real step changes, never on the first
	// paint — the page-open choreography belongs to the head's data-rise.
	let panelAnimationArmed = false
	/** Payload last applied to the closure state above. */
	let appliedPayload: OnboardingPagePayloads | null = null
	let appliedError: Error | null = null
	const onboardingData = createRouteData<'onboarding', OnboardingPagePayloads>({
		locationKey: onboardingDataHref,
		consume(handle, href) {
			if (!isOnboardingPath(href)) return null
			const chooserData = tryConsumeRouteLoaderData(
				handle,
				'onboardingAgentChooser',
				href,
			)
			const routeData = tryConsumeRouteLoaderData(handle, 'onboarding', href)
			if (!routeData) return null
			return {
				onboarding: routeData,
				onboardingAgentChooser: chooserData,
				source: 'snapshot',
			}
		},
		async load(_href, signal) {
			const redirectTo = readOnboardingRedirectTo(handle)
			const payload = await fetchOnboardingPayload(signal)
			if (!payload) {
				throw new Error('Unable to load onboarding.')
			}
			if (payload.loggedIn && !payload.emailVerified) {
				return routeDataRedirect(
					resolveOnboardingPendingVerificationPath(redirectTo),
				)
			}
			return { onboarding: payload, source: 'live' }
		},
	})

	function applyPayload(
		payload: OnboardingPayload,
		source: 'live' | 'snapshot' = 'snapshot',
	) {
		const wasConnected = hasMcpClient
		loggedIn = payload.loggedIn
		mcpServerUrl = payload.mcpServerUrl
		mcpHighlights = payload.mcpHighlights ?? {}
		discoveryPrompt = payload.discoveryPrompt
		hasAccessWin =
			source === 'snapshot'
				? hasAccessWin || payload.hasAccessWin
				: payload.hasAccessWin
		hasSecondMcpClient =
			source === 'snapshot'
				? hasSecondMcpClient || payload.hasSecondMcpClient
				: payload.hasSecondMcpClient
		secondAgentGiftActive = payload.secondAgentStandardGift?.active === true
		connectedAgents =
			source === 'live'
				? (payload.connectedAgents ?? [])
				: mergeConnectedAgents(connectedAgents, payload.connectedAgents)
		hasMcpClient =
			source === 'snapshot'
				? hasMcpClient || payload.hasMcpClient
				: payload.hasMcpClient
		accessWinMemorySubject =
			source === 'snapshot'
				? (accessWinMemorySubject ?? payload.accessWinMemorySubject)
				: payload.accessWinMemorySubject
		persistedPackageName =
			source === 'snapshot'
				? (persistedPackageName ?? payload.persistedPackageName)
				: payload.persistedPackageName
		status = 'ready'
		message = null
		if (source === 'live') discardRenderPrefetches()
		if (!initializedStep) {
			initializedStep = true
			return
		}
		if (!wasConnected && hasMcpClient) {
			panelAnimationArmed = true
			pendingAdvanceToAccess = true
		}
	}

	function buildStepHref(
		step: OnboardingStep,
		href = readCurrentRouterHref(handle),
	) {
		const current = new URL(href, 'https://kody.local')
		return onboardingWizardStepHref(step, current.search)
	}

	function goToStep(step: OnboardingStep) {
		if (typeof window === 'undefined') return
		navigate(buildStepHref(step, window.location.href), {
			preventScrollReset: true,
		})
	}

	function flushPendingAdvanceToAccess(defer: boolean) {
		if (!pendingAdvanceToAccess) return
		if (!defer) {
			pendingAdvanceToAccess = false
			goToStep(2)
			return
		}
		// Route navigate is async. Loader consumption and its corrective
		// render both run around this paint, so keep the flag until the
		// queued task actually navigates.
		handle.queueTask((signal) => {
			if (signal.aborted || !pendingAdvanceToAccess) return
			pendingAdvanceToAccess = false
			goToStep(2)
		})
	}

	let agentChooser: OnboardingAgentChooserPick | null = null

	function selectStep(step: OnboardingStep) {
		panelAnimationArmed = true
		goToStep(step)
		handle.queueTask((signal) => {
			if (signal.aborted) return
			document
				.getElementById(onboardingWizardStepByNumber(step).panelId)
				?.querySelector('h2')
				?.focus({ preventScroll: true })
		})
	}

	async function refreshOnboardingAfterInstall() {
		try {
			const payload = await fetchOnboardingPayload(handle.signal, {
				fresh: true,
			})
			if (handle.signal.aborted || !payload) return
			applyPayload(payload, 'live')
			flushPendingAdvanceToAccess(false)
			handle.update()
		} catch {
			// Install already succeeded in the card; the next poll retries.
		}
	}

	/**
	 * Prototype's `panel-enter` + `art-settle`: the fresh panel slides up
	 * while the mascot settles into its hand-placed tilt. WAAPI instead of a
	 * CSS class so it is enhance-only by construction and never replays on
	 * hydration.
	 */
	function panelEntrance() {
		return ref((node: Element) => {
			if (!panelAnimationArmed) return
			if (matchMedia('(prefers-reduced-motion: reduce)').matches) return
			node.animate(
				[
					{ opacity: 0, translate: '0 12px' },
					{ opacity: 1, translate: '0 0' },
				],
				{ duration: 360, easing: transitions.easeOutValue },
			)
			const art = node.querySelector('[data-panel-art]')
			if (art instanceof HTMLElement) {
				const tilt = art.style.getPropertyValue('--tilt') || '0deg'
				art.animate(
					[
						{ opacity: 0, rotate: '0deg', scale: '0.92' },
						{ opacity: 1, rotate: tilt, scale: '1' },
					],
					{ duration: 560, easing: transitions.easeOutValue },
				)
			}
		})
	}

	// Users typically keep this page open while their MCP client connects
	// or a Step 2 first-search / second grant lands, so poll the same JSON
	// endpoint until those signals arrive without a manual refresh.
	//
	// The interval must stay clear of 5000ms: workerd's HTTP server closes
	// idle keep-alive connections after exactly 5s (kj pipeline timeout), so
	// a 5s poll makes every request race the close. wrangler >= 4.114 turns
	// that race's "Network connection lost" ProxyWorker error into a fatal
	// dev-server exit (cloudflare/workers-sdk#14926). Polling faster than 5s
	// keeps the connection warm so the race never happens.
	const onboardingProgressPollIntervalMs = 4000
	let pollIntervalId: ReturnType<typeof setInterval> | undefined
	let pollInFlight = false

	async function pollOnboardingProgress() {
		if (pollInFlight || status !== 'ready' || !loggedIn) return
		if (hasMcpClient && hasAccessWin && hasSecondMcpClient) {
			return
		}
		if (document.hidden) return
		if (!isOnboardingPath(readCurrentRouterHref(handle))) return
		pollInFlight = true
		try {
			const payload = await fetchOnboardingPayload(handle.signal, {
				fresh: true,
			})
			if (handle.signal.aborted || !payload) return
			if (
				payload.hasMcpClient === hasMcpClient &&
				payload.hasAccessWin === hasAccessWin &&
				payload.hasSecondMcpClient === hasSecondMcpClient &&
				payload.secondAgentStandardGift?.active === secondAgentGiftActive &&
				payload.accessWinMemorySubject === accessWinMemorySubject &&
				payload.persistedPackageName === persistedPackageName &&
				sameConnectedAgents(payload.connectedAgents, connectedAgents)
			) {
				return
			}
			applyPayload(payload, 'live')
			flushPendingAdvanceToAccess(false)
			handle.update()
		} catch {
			// Transient poll failures are fine; the next tick retries.
		} finally {
			pollInFlight = false
		}
	}

	if (typeof document !== 'undefined') {
		if (closeOnboardingMcpOAuthPopupIfOpened()) {
			// This tab was the authorize popup. The opener stays on the wizard.
		}
		pollIntervalId = setInterval(
			pollOnboardingProgress,
			onboardingProgressPollIntervalMs,
		)
		handle.signal.addEventListener('abort', () => clearInterval(pollIntervalId))
		listenForOnboardingMcpOAuthDone(() => {
			void refreshOnboardingAfterInstall()
		}, handle.signal)
	}

	return () => {
		const currentHref = readCurrentRouterHref(handle)
		const snapshot = onboardingData.read(handle, currentHref)
		if (snapshot.data && snapshot.data !== appliedPayload) {
			appliedPayload = snapshot.data
			const { onboarding, onboardingAgentChooser, source } = snapshot.data
			if (onboardingAgentChooser) {
				rememberOnboardingAgentChooser(onboardingAgentChooser)
				agentChooser = onboardingAgentChooser
			}
			if (onboarding.loggedIn && !onboarding.emailVerified) {
				window.location.assign(
					resolveOnboardingPendingVerificationPath(
						readOnboardingRedirectTo(handle),
					),
				)
			} else {
				applyPayload(onboarding, source)
				if (source === 'live' && !agentChooser) {
					agentChooser = resolveOnboardingAgentChooser()
				}
			}
		}
		if (snapshot.error && snapshot.error !== appliedError) {
			appliedError = snapshot.error
			status = 'error'
			message = snapshot.error.message
		}
		const busy = snapshot.kind === 'pending' && appliedPayload !== null
		flushPendingAdvanceToAccess(true)

		const location = readOnboardingLocation(currentHref)
		const activeStep: OnboardingStep = location?.valid
			? location.step
			: hasMcpClient
				? 2
				: 1
		const selectedAgent = location?.valid ? location.agent : null
		if (activeStep === 1 && selectedAgent) {
			rememberOnboardingSelectedAgent(selectedAgent)
		}
		const firstAgent = resolveOnboardingFirstAgentKind(
			readRememberedOnboardingSelectedAgent(),
			connectedAgents,
		)
		const visibleSelectedAgent =
			activeStep === 3
				? resolveOnboardingStep3SelectedAgent(
						firstAgent,
						selectedAgent,
						connectedAgents,
					)
				: selectedAgent
		if (
			activeStep === 3 &&
			selectedAgent &&
			visibleSelectedAgent == null &&
			typeof window !== 'undefined'
		) {
			handle.queueTask((signal) => {
				if (signal.aborted) return
				navigate(onboardingSecondAgentHref(null, readRouterSearch(handle)), {
					preventScrollReset: true,
				})
			})
		}
		const selectedAgentLabel = visibleSelectedAgent
			? onboardingAgentLabel(visibleSelectedAgent)
			: null
		const connectedAgentLabel =
			firstAgent && firstAgent !== 'other'
				? onboardingAgentLabel(firstAgent)
				: null

		return (
			<section mix={css(onboardCss)} aria-busy={busy ? 'true' : undefined}>
				{busy ? renderRoutePendingStatus() : null}
				<header mix={css(onboardHeadCss)}>
					<h1 data-rise style={{ '--rise': '0' }}>
						Get started with <em>Kody</em>
					</h1>
				</header>

				{message ? (
					<p mix={css(errorMessageCss)} role="alert">
						{message}
					</p>
				) : null}

				{status === 'ready' ? (
					<>
						{renderWizardStepsNav({
							activeStep,
							hasMcpClient,
							accessWin: hasAccessWin,
							hasSecondMcpClient,
							stepHref: (step) => buildStepHref(step, currentHref),
						})}

						{activeStep === 1
							? renderConnectAgentPanel({
									entrance: panelEntrance(),
									activeStep,
									onSelectStep: selectStep,
									hasMcpClient,
									loggedIn,
									connectedAgents,
									selectedAgent: visibleSelectedAgent,
									selectedAgentLabel,
									agentChooser,
									mcpServerUrl,
									mcpHighlights: mcpHighlights ?? {},
									search: readRouterSearch(handle),
								})
							: null}

						{activeStep === 2
							? renderAccessPanel({
									entrance: panelEntrance(),
									activeStep,
									onSelectStep: selectStep,
									hasMcpClient,
									hasAccessWin,
									discoveryPrompt,
									selectedAgentLabel: connectedAgentLabel,
									connectedAgents,
									search: readRouterSearch(handle),
								})
							: null}

						{activeStep === 3
							? renderSecondAgentPanel({
									entrance: panelEntrance(),
									activeStep,
									onSelectStep: selectStep,
									loggedIn,
									hasSecondMcpClient,
									secondAgentGiftActive,
									connectedAgents,
									firstAgent,
									selectedAgent: visibleSelectedAgent,
									selectedAgentLabel,
									agentChooser,
									mcpServerUrl,
									mcpHighlights: mcpHighlights ?? {},
									search: readRouterSearch(handle),
									accessWinMemorySubject,
									persistedPackageName,
								})
							: null}
					</>
				) : null}

				<p data-rise style={{ '--rise': '1' }} mix={css(discordInviteWrapCss)}>
					<a
						href={routes.discord.href()}
						mix={css(discordInviteLinkCss)}
						data-testid="onboarding-join-discord"
					>
						<ProviderIcon providerId="discord" size="1.1em" />
						Join the Discord
					</a>
				</p>
			</section>
		)
	}
}

const onboardCss = {
	maxWidth: '56rem',
	marginInline: 'auto',
	padding:
		'clamp(2.5rem, 6vw, 4.5rem) clamp(1.25rem, 4vw, 2.5rem) clamp(3rem, 7vw, 5rem)',
}

/* The shirt fabric welcomes you in, same whisper as the landing close. */
const onboardHeadCss = {
	position: 'relative' as const,
	/* See `pageHeadCss`: the fabric is a backdrop, so it paints under the head. */
	isolation: 'isolate' as const,
	'&::before': {
		content: '""',
		position: 'absolute' as const,
		zIndex: -1,
		inset: '-60% -12% -140%',
		background: `radial-gradient(ellipse 42% 58% at 68% 40%, oklch(from ${colors.text} l c h / 0.05), transparent 72%)`,
		maskImage: 'var(--kody-pattern)',
		maskPosition: 'center',
		maskSize: '340px',
		maskRepeat: 'repeat',
		WebkitMaskImage: 'var(--kody-pattern)',
		WebkitMaskPosition: 'center',
		WebkitMaskSize: '340px',
		WebkitMaskRepeat: 'repeat',
		pointerEvents: 'none' as const,
	},
	'& h1': {
		margin: 0,
		fontSize: 'clamp(2.2rem, 4.5vw, 3.2rem)',
		fontWeight: 760,
		letterSpacing: '-0.028em',
		lineHeight: 1.04,
	},
	'& h1 em': {
		fontStyle: 'normal',
		color: colors.primaryText,
	},
}

const errorMessageCss = {
	margin: 'clamp(2.2rem, 5vw, 3.2rem) 0 0',
	color: colors.error,
}

/* Nested surfaces step down to the page ground so they read as wells. */
const discordInviteWrapCss = {
	margin: 'clamp(2rem, 4.5vw, 2.8rem) 0 0',
}

const discordInviteLinkCss = {
	...getGhostButtonCss({ size: 'sm' }),
	width: 'fit-content',
	gap: '0.4rem',
	padding: '0.4rem 0.85rem 0.4rem 0.65rem',
	font: `600 0.92rem/1 ${typography.fontFamilyBody}`,
}
